/**
 * The single point where a template API response becomes a working document (B7, contract §0).
 *
 * The document round-trips through a PHP array, so every empty map inside `data` comes back as `[]`
 * (`data.rubrics` above all, which makes the Zod record parse throw). `show`, `store`, `update` and
 * `duplicate` all return a full document, so all four funnel through here — otherwise a save
 * response refreshes the working document unrepaired and the *next* save fails.
 *
 * It is also where the response-only signed `media` map is drained into the link cache and dropped
 * (B13, media contract §2), so every one of those four gets its images pre-resolved.
 *
 * It does NOT validate (B9): Zod strips unknown keys and `PATCH` is a full replacement, so saving
 * parsed output would permanently destroy fields real rows carry.
 */
import type { AssessmentTemplate } from '../../model/assessments/interfaces'
import type { AssessmentTemplateData } from '../../model/assessments/interfaces'
import type { AssessmentTemplateDocument } from '../../model/assessments/types'

import { rememberAssessmentMediaLink } from '../../model/assessments/media-asset.hook'
import { normalizeAssessmentDocument } from '../../lib/assessments/normalize-document'

/**
 * Seeds the link cache from the response's signed `media` map (media contract §2), so the editor's
 * first paint renders every stored image with zero `GET .../media` round-trips.
 *
 * An absent map is normal, not an error: it only exists as of the backend half of this design.
 * `media_expires_at` is one expiry for the whole map, and entries keep the map's own keys — the
 * strings `useAssessmentMediaSrc` looks up.
 */
const seedMediaLinks = (template: AssessmentTemplate): void => {
  const map = template.media
  const expiresAt = template.media_expires_at

  if (!map || !expiresAt) {
    return
  }

  Object.entries(map).forEach(([path, url]) => {
    if (typeof path === 'string' && typeof url === 'string' && path && url) {
      rememberAssessmentMediaLink({ path, url, expires_at: expiresAt })
    }
  })
}

/**
 * Takes the `data` of a `show` / `store` / `update` / `duplicate` response and returns the template
 * with `template.data` normalised. `null` when the envelope carries no template, so callers treat
 * "no document" like any other failure.
 *
 * THE CAST — `normalizeAssessmentDocument` is `(raw: unknown) => unknown`: it repairs, it does not
 * check. The wire shape is guaranteed by `api-contract.md`; the document's validity never is (§A-3:
 * the backend stores `data` verbatim), which is why the editor's Zod check exists.
 */
export const toAssessmentTemplate = (payload: AssessmentTemplateData | null | undefined): AssessmentTemplate | null => {
  const template = payload?.template
  if (!template) return null

  seedMediaLinks(template)

  return {
    ...template,
    data: normalizeAssessmentDocument(template.data) as AssessmentTemplateDocument,
  }
}
